import { z } from 'zod';

// T-436 — MODELLO DIETE DISTILLATO (ristorazione collettiva / ospedaliera).
//
// Vocabolario UNICO delle tipologie di dieta usato dall'ordine per LETTO dei
// reparti (wizard) e dalla scheda ordine sintetizzata del preposto. È il "modello
// distillato" richiesto dall'owner: si estrae dai moduli reali dei reparti e si
// standardizza in un elenco unico che vada bene per tutti.
//
// FONTE v2 (2026-07-07) — DISTILLATA dai MODULI REPARTO REALI (Bluecoop/Moduli
// ordini reparti/, 8 fogli letti dopo conversione sips per aggirare com.apple.macl)
// + scheda preposto "PRANZO DEL". Unione delle colonne dieta dei reparti:
//  - Ostetricia/Ginecologia (PRN R 0 M.01): Aproteica, Iposodica, Diabetica,
//    Celiaca, Pastina frutta cotta, Idrica, No verd/carne cruda/insaccati, Allergie.
//  - Ortopedia/Traumatologia: + Diabetica ipos, Semiliquida, Liquida.
//  - Medicina d'Urgenza / Pronto Soccorso: Diabetico, Nefropatico, Frullato,
//    Semiliquida, Intolleranze(note), Libera(=ordinaria), Digiuno esami, Parenterale.
//  - Foglio SALA (ricco): + Ipocalorica, Completa(=ordinaria), Semisolida/Omogenea/Purea.
//  - Hospice / Cardiologia: Aproteica, Iposodica, Diabetica, Celiaca, Frullata,
//    Semiliquida, Liquida, Diabetica iposodica.
//  - Rianimazione: + Azotemica.
// Note fini scritte a mano (pasta corta, secondo morbido, frutta cotta, purea,
// con addensante, no suino, no frutta secca, no latte/latticini) → campo NOTE.
//
// PRINCIPIO: la DIETA è la tipologia del paziente (etichetta). Le varianti di
// piatto/consistenza fini ("con frutta cotta", "carne frullata"…) e le allergie
// si scrivono nelle NOTE della riga letto. L'aggancio dieta→ricetta del giorno
// avviene DOPO, in Pianifica (T-431). Questo elenco è ESTENSIBILE: quando
// arrivano nuovi moduli dai reparti si aggiungono voci qui (append), senza
// toccare UI né sync (è una costante shared).

export const COLLETTIVA_DIET_GROUPS = [
  'ordinaria',
  'terapeutica',
  'consistenza',
  'speciale',
  'personalizzata',
] as const;
export type CollettivaDietGroup = (typeof COLLETTIVA_DIET_GROUPS)[number];

export const COLLETTIVA_DIET_GROUP_LABELS: Record<CollettivaDietGroup, string> = {
  ordinaria: 'Ordinaria',
  terapeutica: 'Terapeutica',
  consistenza: 'Consistenza modificata',
  speciale: 'Digiuno / speciale',
  personalizzata: 'Personalizzata',
};

export interface CollettivaDiet {
  /** Codice stabile (kebab/snake), NON tradurre né rinominare: viaggia sui dati. */
  code: string;
  /** Etichetta mostrata nel menu a tendina e sulle stampe. */
  label: string;
  group: CollettivaDietGroup;
  /** Sinonimi/varianti dai moduli reali → per match/normalizzazione futura. */
  aliases?: string[];
}

// v2 distillata dai moduli reparto reali. Ordine = comparsa nel menu (per gruppo).
// I codici sono STABILI: le voci nuove si aggiungono in coda al gruppo.
export const COLLETTIVA_DIETS: readonly CollettivaDiet[] = [
  // — Ordinaria (nessuna dieta speciale: casella vuota sui moduli) —
  { code: 'ordinaria', label: 'Ordinaria', group: 'ordinaria', aliases: ['ordinario', '1° ordinario', '2° ordinario', 'contorno ordinario', 'libera', 'completa', 'pasti ordinari', 'pasto ordinario'] },

  // — Terapeutica —
  { code: 'diabetica', label: 'Diabetica', group: 'terapeutica', aliases: ['diabetico', 'ordinario diabetico', 'ord diab', 'ordinaria diabetica'] },
  { code: 'iposodica', label: 'Iposodica', group: 'terapeutica', aliases: ['iposodico', 'orda iposodico', 'ord iposo', 'ordinario iposodico', 'ordinaria iposodica'] },
  { code: 'diabetica_iposodica', label: 'Diabetica iposodica', group: 'terapeutica', aliases: ['diabetica ipos', 'diab iposodica', 'ordinario diabetico iposodico'] },
  { code: 'aproteica', label: 'Aproteica (renale)', group: 'terapeutica', aliases: ['apro', 'pasta aproteica', 'liquida aproteica', 'nefropatica', 'nefropatico', 'azotemica', 'azotemico'] },
  { code: 'celiaca', label: 'Celiaca / senza glutine', group: 'terapeutica', aliases: ['celiaco', 'senza glutine', 'gluten free'] },
  { code: 'ipocalorica', label: 'Ipocalorica', group: 'terapeutica', aliases: ['ipocalorico', 'dieta ipocalorica'] },
  { code: 'senza_latticini', label: 'Senza latticini', group: 'terapeutica', aliases: ['ordinario no latticini', 'no latticini', 'no lat', 'no latte', 'senza lattosio'] },
  { code: 'protetta', label: 'No verdura/carne cruda/insaccati', group: 'terapeutica', aliases: ['immunodepressi', 'no crudo', 'protetta', 'no verd carne cruda insaccati'] },
  { code: 'idrica', label: 'Idrica (solo liquidi)', group: 'terapeutica', aliases: ['acqua', 'dieta idrica'] },

  // — Consistenza modificata (disfagia / texture) —
  // Owner 2026-07-08: rimossi 'pastina' + 'riso_bianco' (→ Dieta astringente);
  // 'leggera' splittata in 'leggera_vegetale' + 'dieta_bianco'; aggiunte
  // 'liquida_densa' e 'dieta_morbida'.
  { code: 'semiliquida', label: 'Semiliquida', group: 'consistenza', aliases: ['semiliquida monitor', 'semiliquida in bianco'] },
  { code: 'liquida', label: 'Liquida', group: 'consistenza', aliases: ['liquida con frutta cotta'] },
  { code: 'liquida_densa', label: 'Liquida densa', group: 'consistenza', aliases: ['liquida addensata', 'addensata'] },
  { code: 'frullata', label: 'Frullata (carne frullata)', group: 'consistenza', aliases: ['frullato', 'car. frul', 'carne frullata', 'semiliquida carne frullata'] },
  { code: 'omogenizzato', label: 'Omogeneizzato / purea', group: 'consistenza', aliases: ['con omogenizzato', 'omogeneizzato', 'omog', 'semisolida', 'omogenea', 'purea'] },
  { code: 'dieta_morbida', label: 'Dieta morbida', group: 'consistenza', aliases: ['morbida', 'morbido', 'secondo morbido', 'cibo morbido'] },
  { code: 'astringente', label: 'Dieta astringente', group: 'consistenza', aliases: ['astringente', 'riso in bianco', 'riso b.', 'pastina', 'pastina bianca', 'pastina frutta cotta', 'pastina b.'] },
  { code: 'leggera_vegetale', label: 'Leggera vegetale', group: 'consistenza', aliases: ['leggera vegetale', 'leggera', 'leggera con riso'] },
  { code: 'dieta_bianco', label: 'Dieta in bianco', group: 'consistenza', aliases: ['in bianco', 'dieta in bianco', 'sabbiolina'] },

  // — Digiuno / speciale —
  { code: 'digiuno', label: 'Digiuno (per esami)', group: 'speciale', aliases: ['digiuno esami', 'a digiuno', 'solo digiuno colazione'] },
  { code: 'parenterale', label: 'Parenterale (non per bocca)', group: 'speciale', aliases: ['parenterale', 'nutrizione parenterale'] },

  // — Personalizzata —
  { code: 'personalizzata', label: 'Personalizzata (vedi note)', group: 'personalizzata', aliases: ['dieta 1', 'dieta 2', 'dieta 3', 'dieta personalizzata'] },
];

// Codici validi (per la validazione della riga letto).
export const COLLETTIVA_DIET_CODES = COLLETTIVA_DIETS.map((d) => d.code) as [string, ...string[]];

const DIET_BY_CODE = new Map<string, CollettivaDiet>(COLLETTIVA_DIETS.map((d) => [d.code, d]));

/** Etichetta di una dieta dal codice; ritorna il codice stesso se sconosciuto. */
export function collettivaDietLabel(code: string | null | undefined): string {
  if (!code) return '';
  return DIET_BY_CODE.get(code)?.label ?? code;
}

/** Diete raggruppate per famiglia (per gli optgroup del menu a tendina). */
export function collettivaDietsByGroup(): Array<{ group: CollettivaDietGroup; label: string; diets: CollettivaDiet[] }> {
  return COLLETTIVA_DIET_GROUPS.map((g) => ({
    group: g,
    label: COLLETTIVA_DIET_GROUP_LABELS[g],
    diets: COLLETTIVA_DIETS.filter((d) => d.group === g),
  })).filter((s) => s.diets.length > 0);
}

const normalize = (s: string): string =>
  s
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();

// Indice etichetta/alias normalizzati → codice, per il match ESATTO.
const DIET_INDEX: ReadonlyMap<string, string> = (() => {
  const m = new Map<string, string>();
  for (const d of COLLETTIVA_DIETS) {
    m.set(normalize(d.label), d.code);
    m.set(normalize(d.code), d.code);
    for (const a of d.aliases ?? []) m.set(normalize(a), d.code);
  }
  return m;
})();

// Alias generici che NON devono fare match a sottostringa (altrimenti 'ordinario'
// vince su 'ordinario diabetico'): restano validi solo per il match ESATTO.
const GENERIC_EXACT_ONLY = new Set(
  ['ordinario', 'ordinaria', '1 ordinario', '2 ordinario', 'libera', 'completa', 'pasti ordinari', 'pasto ordinario'].map(normalize),
);

// Candidati per il match a sottostringa: esclusi i generici, ordinati per
// lunghezza chiave DECRESCENTE (la chiave più specifica vince).
const SUBSTRING_CANDIDATES: ReadonlyArray<readonly [string, string]> = Array.from(DIET_INDEX.entries())
  .filter(([key]) => !GENERIC_EXACT_ONLY.has(key))
  .sort((a, b) => b[0].length - a[0].length);

/**
 * Mappa un testo libero a un codice dieta noto. Prima match ESATTO normalizzato,
 * poi a sottostringa con la chiave più specifica per prima. Il ramo "reverse"
 * (chiave contiene input, per abbreviazioni tipo "diab"⊂"diabetico") è ristretto
 * alle chiavi a TOKEN SINGOLO per evitare falsi positivi come
 * "frutta cotta" ⊂ "liquida con frutta cotta". Ritorna null se non riconosciuto.
 */
export function matchCollettivaDiet(text: string | null | undefined): string | null {
  if (!text) return null;
  const n = normalize(text);
  if (!n) return null;
  const exact = DIET_INDEX.get(n);
  if (exact) return exact;
  for (const [key, code] of SUBSTRING_CANDIDATES) {
    if (key.length < 4) continue;
    if (n.includes(key)) return code;
    if (!key.includes(' ') && n.length >= 4 && key.includes(n)) return code;
  }
  return null;
}

// Zod: codice dieta (accetta anche codici futuri non ancora in elenco → string
// vincolata, non enum, così i dati non si rompono se l'elenco evolve).
export const CollettivaDietCodeSchema = z.string().min(1).max(60);
